import React, { memo, useCallback } from 'react';
import { Avatar, Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';

import { IAlyceCustomTheme } from '../../../../../styles/alyceTheme';
import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import StyledSecondaryButton from '../../../../../components/Shared/StyledSecondaryButton';
import { getCanBeDonated, getCanExchangeAndDonate, getGiftGl } from '../../../store/data/data.selectors';
import { getDetectedCountryByCode } from '../../../../../store/geoIpCountry/geoIpCountry.selectors';
import { setCountryId } from '../../../store/giftFlow/giftFlow.actions';
import { skipDetectCountryFlow } from '../../../../../store/geoIpCountry/geoIpCountry.actions';
import { getIsActivate } from '../../../store/statuses/statuses.selectors';

import ButtonsDivider from './ButtonsDivider';
import FlowWrapper from './FlowWrapper';

const useStyles = makeStyles<IAlyceCustomTheme>(({ palette, spacing }) => ({
  buttonText: {
    lineHeight: '1.15 !important',
    color: `${palette.customStyling.primaryCtaColor} !important`,
  },
  countryFlag: {
    width: 28,
    height: 28,
    marginRight: spacing(1),
  },
  localMarketplaceHint: {
    fontFamily: 'Lato',
    fontSize: 14,
    color: palette.text.primary,
    textAlign: 'center',
    marginBottom: spacing(1),
  },
}));

export interface ICampaignCountryFlowProps {
  onExchange: (useDonation: boolean) => void;
  onDonate: () => void;
}

const CampaignCountryFlow = ({ onExchange, onDonate }: ICampaignCountryFlowProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const gl = useSelector(getGiftGl);
  const detectedCountry = useSelector(getDetectedCountryByCode);
  const isActivate = useSelector(getIsActivate);
  const canBeDonated = useSelector(getCanBeDonated);
  const canExchangeAndDonate = useSelector(getCanExchangeAndDonate);

  const isDonationAllowed = canBeDonated && !isActivate;
  const isExchangeAndDonateAllowed = canExchangeAndDonate && !isActivate;

  const setDetectedCountryAsMarketplaceCountry = useCallback(() => {
    if (detectedCountry) {
      dispatch(setCountryId(detectedCountry.id));
    }
  }, [dispatch, detectedCountry]);

  const handleSubmitLocalMarketplace = useCallback(() => {
    setDetectedCountryAsMarketplaceCountry();
    onExchange(false);
  }, [onExchange, setDetectedCountryAsMarketplaceCountry]);

  const handleContinueAnyway = useCallback(() => {
    dispatch(skipDetectCountryFlow());
  }, [dispatch]);

  const handleDonate = useCallback(() => {
    setDetectedCountryAsMarketplaceCountry();
    onDonate();
  }, [onDonate, setDetectedCountryAsMarketplaceCountry]);

  return (
    <FlowWrapper>
      <Grid container direction="row">
        <Grid item xs={12}>
          <Typography className={classes.localMarketplaceHint}>
            Do you want to select a gift from your local marketplace?
          </Typography>
          <StyledSubmitButton
            data-testid="exchangeLocalMarketplace"
            width="100%"
            onClick={handleSubmitLocalMarketplace}
            variant="outlined"
          >
            <Box display="flex" width={1} alignItems="center" justifyContent="center">
              <Avatar className={classes.countryFlag} alt="Detected country flag" src={detectedCountry?.image} />
              <span className={classes.buttonText}>{detectedCountry?.name} Marketplace</span>
            </Box>
          </StyledSubmitButton>
        </Grid>
      </Grid>
      <ButtonsDivider />
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} sm={isExchangeAndDonateAllowed ? 6 : 12}>
          <StyledSecondaryButton onClick={handleContinueAnyway}>Continue anyway</StyledSecondaryButton>
        </Grid>
        {isDonationAllowed && (
          <Grid item xs={12} sm={isExchangeAndDonateAllowed ? 6 : 12}>
            <StyledSecondaryButton onClick={handleDonate}>Donate {gl} value</StyledSecondaryButton>
          </Grid>
        )}
      </Grid>
    </FlowWrapper>
  );
};

export default memo<ICampaignCountryFlowProps>(CampaignCountryFlow);
